import { useCallback, useEffect, useRef, Children } from 'react';

import useDebounce from '@picsart/rc/useDebounce';

const useMasonry = ({ children, columnCount, gap }) => {
  const gridRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const columnHeights = useRef<number[]>(Array(columnCount).fill(0));

  const calculatePositions = useCallback(() => {
    if (!gridRef.current) return;

    const gridWidth = gridRef.current.clientWidth;
    const itemWidth = (gridWidth - gap * (columnCount - 1)) / columnCount;

    columnHeights.current.fill(0);

    Children.forEach(children, (_, index) => {
      const itemElement = itemRefs.current[index];
      if (!itemElement) return;

      const itemHeight = itemElement.clientHeight;
      const columnIndex = columnHeights.current.indexOf(Math.min(...columnHeights.current));
      const left = columnIndex * (itemWidth + gap);
      const top = columnHeights.current[columnIndex];

      requestAnimationFrame(() => {
        Object.assign(itemElement.style, {
          width: `${itemWidth}px`,
          left: `${left}px`,
          top: `${top}px`,
        });
      });

      columnHeights.current[columnIndex] += itemHeight + gap;
    });

    if (gridRef.current) {
      gridRef.current.style.height = `${Math.max(...columnHeights.current)}px`;
    }
  }, [children, columnCount, gap]);

  const debouncedCalculatePositions = useCallback(useDebounce(calculatePositions, 100), [
    calculatePositions,
  ]);

  useEffect(() => {
    if (!gridRef.current) return;

    const resizeObserver = new ResizeObserver(debouncedCalculatePositions);
    resizeObserver.observe(gridRef.current);

    calculatePositions();

    // eslint-disable-next-line consistent-return
    return () => {
      resizeObserver.disconnect();
    };
  }, [debouncedCalculatePositions, calculatePositions]);

  useEffect(() => {
    const itemResizeObserver = new ResizeObserver(debouncedCalculatePositions);

    itemRefs.current.forEach(item => {
      if (item) itemResizeObserver.observe(item);
    });

    return () => {
      itemRefs.current.forEach(item => {
        if (item) itemResizeObserver.unobserve(item);
      });
    };
  }, [children, columnCount, debouncedCalculatePositions]);

  return {
    gridRef,
    itemRefs,
  };
};

export default useMasonry;
